import type { QuoteSummary } from '../types/quote.types';
import type { BasketPricingInput, PricedBasket } from '../types/basketPricing.types';
import {
    RetiredSaveError,
    QuoteConflictError,
    UnsaveableDraftError,
    recordFailedPricingInput,
    toQuoteSaveError,
} from './quoteSaveErrors';

export interface QuoteSnapshot {
    draftId: string;
    quoteId: string | undefined;
    version: number | undefined;
    summary: QuoteSummary;
    pricingInput: BasketPricingInput | null;
}

export interface SavedQuote {
    id: string;
    quoteRef: string;
    status: 'live' | 'dead' | 'completed';
    version: number;
    updatedAt: string | null;
}

export type SaveRunKind = 'create' | 'update';

export interface SaveRun {
    kind: SaveRunKind;
    quiet: boolean;
}

export interface PricingOverride {
    pricing: PricedBasket;
    input: BasketPricingInput | null;
}

export interface SaveRequestOptions {
    quiet?: boolean;
    pricingOverride?: PricingOverride;
}

export type SaveLanePhase = 'idle' | 'waiting' | 'running' | 'failed' | 'retired';

export interface SaveLaneState {
    phase: SaveLanePhase;
    isBusy: boolean;
    isUnsaved: boolean;
    lastError: unknown;
    lastSavedAt: string | null;
}

export interface QuoteSaveLaneAdapters {
    draftId: string;
    getDraftId: () => string;
    getSnapshot: () => QuoteSnapshot | null;
    priceBasket: (snapshot: QuoteSnapshot) => Promise<PricedBasket>;
    create: (snapshot: QuoteSnapshot, pricing: PricedBasket) => Promise<SavedQuote>;
    update: (
        snapshot: QuoteSnapshot,
        pricing: PricedBasket,
        expectedVersion: number | undefined,
    ) => Promise<SavedQuote>;
    onSaved: (draftId: string, saved: SavedQuote) => void;
    setDirty: (dirty: boolean) => void;
    onRunStart?: (run: SaveRun) => void;
    onRunSettled?: (run: SaveRun, outcome: 'saved' | Error) => void;
    minIntervalMs: number;
}

export interface QuoteSaveLane {
    request: (options?: SaveRequestOptions) => Promise<void>;
    flush: (options?: SaveRequestOptions) => Promise<void>;
    retire: () => void;
    owns: () => boolean;
    settled: () => Promise<void>;
    getState: () => SaveLaneState;
    subscribe: (listener: () => void) => () => void;
}

interface Deferred {
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: unknown) => void;
}

interface PendingBatch {
    deferred: Deferred;
    quiet: boolean;
    immediate: boolean;
    override: PricingOverride | null;
}

const createDeferred = (): Deferred => {
    let resolve!: () => void;
    let reject!: (error: unknown) => void;
    const promise = new Promise<void>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return { promise, resolve, reject };
};

export const samePricingInput = (a: BasketPricingInput | null, b: BasketPricingInput | null): boolean =>
    JSON.stringify(a) === JSON.stringify(b);

const asError = (error: unknown): Error => {
    if (error instanceof Error) return error;
    if (typeof error !== 'object' || error === null) return new Error(String(error));
    const { message } = error as { message?: unknown };
    return Object.assign(new Error(typeof message === 'string' ? message : 'Request failed'), error);
};

export const createQuoteSaveLane = (adapters: QuoteSaveLaneAdapters): QuoteSaveLane => {
    const { draftId, minIntervalMs } = adapters;
    let phase: SaveLanePhase = 'idle';
    let dirty = false;
    let lastError: unknown = null;
    let lastSavedAt: string | null = null;
    let lastRunStartedAt = Number.NEGATIVE_INFINITY;
    let pending: PendingBatch | null = null;
    let current: Deferred | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let state: SaveLaneState = buildState();
    const listeners = new Set<() => void>();
    const settledWaiters: Deferred[] = [];

    function buildState(): SaveLaneState {
        return {
            phase,
            isBusy: phase === 'waiting' || phase === 'running',
            isUnsaved: dirty || lastError !== null,
            lastError,
            lastSavedAt,
        };
    }

    const emit = () => {
        const next = buildState();
        const unchanged =
            next.phase === state.phase &&
            next.isUnsaved === state.isUnsaved &&
            next.lastError === state.lastError &&
            next.lastSavedAt === state.lastSavedAt;
        if (unchanged) return;
        state = next;
        listeners.forEach((listener) => listener());
    };

    const clearTimer = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const retire = () => {
        if (phase === 'retired') return;
        phase = 'retired';
        clearTimer();
        const error = new RetiredSaveError();
        pending?.deferred.reject(error);
        current?.reject(error);
        settledWaiters.splice(0).forEach((waiter) => waiter.reject(error));
        pending = null;
        current = null;
        emit();
    };

    const ownsDraft = (): boolean => {
        if (phase === 'retired') return false;
        if (adapters.getDraftId() === draftId) return true;
        retire();
        return false;
    };

    const finishRun = (): void => {
        phase = 'idle';
        if (pending !== null) {
            scheduleNext();
            return;
        }
        phase = lastError === null ? 'idle' : 'failed';
        settledWaiters.splice(0).forEach((waiter) => waiter.resolve());
        emit();
    };

    const scheduleNext = (): void => {
        const batch = pending;
        if (batch === null) return;
        const remaining = lastRunStartedAt + minIntervalMs - Date.now();
        if (!batch.immediate && minIntervalMs > 0 && remaining > 0) {
            phase = 'waiting';
            if (timer === null) {
                timer = setTimeout(() => {
                    timer = null;
                    void startRun();
                }, remaining);
            }
            emit();
            return;
        }
        void startRun();
    };

    const performSave = async (
        snapshot: QuoteSnapshot,
        batch: PendingBatch,
        run: SaveRun,
    ): Promise<SavedQuote | null> => {
        const override =
            batch.override !== null && samePricingInput(batch.override.input, snapshot.pricingInput)
                ? batch.override.pricing
                : null;
        const pricing = override ?? (await adapters.priceBasket(snapshot));
        if (!ownsDraft()) return null;
        const saved =
            run.kind === 'create'
                ? await adapters.create(snapshot, pricing)
                : await adapters.update(snapshot, pricing, snapshot.version);
        return ownsDraft() ? saved : null;
    };

    const settleFailure = (
        batch: PendingBatch,
        run: SaveRun,
        failure: unknown,
        pricingInput: BasketPricingInput | null,
    ): void => {
        const error = toQuoteSaveError(failure);
        recordFailedPricingInput(error, pricingInput);
        lastError = error;
        adapters.onRunSettled?.(run, asError(error));
        batch.deferred.reject(error);
        if (pending !== null && error instanceof QuoteConflictError) {
            pending.deferred.reject(error);
            pending = null;
        }
    };

    const settleUnsaveable = (batch: PendingBatch): void => {
        const error = new UnsaveableDraftError();
        lastError = error;
        batch.deferred.reject(error);
    };

    const settleSuccess = (batch: PendingBatch, run: SaveRun, saved: SavedQuote): void => {
        adapters.onSaved(draftId, saved);
        lastError = null;
        lastSavedAt = saved.updatedAt;
        adapters.onRunSettled?.(run, 'saved');
        batch.deferred.resolve();
    };

    async function startRun(): Promise<void> {
        const batch = pending;
        if (batch === null || phase === 'retired' || phase === 'running') return;
        if (!ownsDraft()) return;
        pending = null;
        current = batch.deferred;
        clearTimer();
        phase = 'running';
        lastRunStartedAt = Date.now();
        emit();

        const snapshot = adapters.getSnapshot();
        if (snapshot !== null && snapshot.draftId !== draftId) {
            retire();
            return;
        }
        if (snapshot === null) {
            settleUnsaveable(batch);
            current = null;
            finishRun();
            return;
        }

        const run: SaveRun = { kind: snapshot.quoteId ? 'update' : 'create', quiet: batch.quiet };
        adapters.onRunStart?.(run);
        try {
            const saved = await performSave(snapshot, batch, run);
            if (saved === null || !ownsDraft()) {
                adapters.onRunSettled?.(run, new RetiredSaveError());
                return;
            }
            settleSuccess(batch, run, saved);
        } catch (failure) {
            if (!ownsDraft()) {
                adapters.onRunSettled?.(run, new RetiredSaveError());
                return;
            }
            settleFailure(batch, run, failure, snapshot.pricingInput);
        }
        current = null;
        if (pending === null && lastError === null) {
            dirty = false;
            adapters.setDirty(false);
        }
        finishRun();
    }

    const enqueue = (options: SaveRequestOptions, immediate: boolean): Promise<void> => {
        if (!ownsDraft()) return Promise.reject(new RetiredSaveError());
        dirty = true;
        adapters.setDirty(true);
        const batch: PendingBatch = pending ?? {
            deferred: createDeferred(),
            quiet: true,
            immediate: false,
            override: null,
        };
        batch.quiet = batch.quiet && options.quiet === true;
        batch.immediate = batch.immediate || immediate;
        if (options.pricingOverride) {
            batch.override = options.pricingOverride;
        }
        pending = batch;
        if (phase !== 'running') scheduleNext();
        else emit();
        return batch.deferred.promise;
    };

    return {
        request: (options = {}) => enqueue(options, minIntervalMs <= 0),
        flush: (options = {}) => enqueue(options, true),
        retire,
        owns: ownsDraft,
        settled: () => {
            if (phase === 'retired') return Promise.reject(new RetiredSaveError());
            if (phase === 'idle' || phase === 'failed') return Promise.resolve();
            const waiter = createDeferred();
            settledWaiters.push(waiter);
            return waiter.promise;
        },
        getState: () => state,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
};
